/**
 * Sewing Efficiency — `<DataTable>` columns for the four views.
 *
 * Structure is the server's: alignment, the sortable whitelist (§5.4) and the declared type arrive
 * with each column and none is guessed here (§4.3). Efficiency arrives as a 0–1 fraction and is
 * scaled to a percentage once, in {@link formatSewingCell}. The four grids are authored layouts —
 * they name the columns they want in the order they want them, which is presentation and nothing
 * more: no column is derived, combined or recomputed.
 *
 * `design-notes.md` in this folder carries why each layout is shaped as it is.
 */

import type { ColumnDef } from '@tanstack/react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightLong } from '@fortawesome/free-solid-svg-icons';
import { format, isValid, parseISO } from 'date-fns';

import { Button } from '@/components/ui/button';
import { HEADER_TITLE, WRAP_HEADER } from '@/components/DataTable';
import { decimal, defaultFormat } from '@/components/report/groupedColumns';
import type { ReportColumn } from '@/lib/api';

import type { ChartMode, Metric, ReportViewName } from './filters';
import { STANDING, StandingArrow, standing } from './target';

/** A row of any of the three views. Values are already in their declared type (§5.7). */
export type SewingRow = Record<string, unknown>;

/** The two efficiency columns of one metric family. */
interface MetricFields {
  efficiency: string;
  ppaEfficiency: string;
}

const METRIC_FIELDS: Record<Metric, MetricFields> = {
  EINS: { efficiency: 'eins_efficiency', ppaEfficiency: 'eins_ppa_efficiency' },
  SFTS: { efficiency: 'sfts_efficiency', ppaEfficiency: 'sfts_ppa_efficiency' },
};

/** The efficiency column actually on screen, which the chart and the table must agree on. */
export function efficiencyField(
  metric: Metric,
  includePpa: boolean,
  available: ReadonlySet<string>,
): string {
  const fields = METRIC_FIELDS[metric];
  // Tests `available`, not the PPA toggle: `daily` returns no PPA efficiency column at all.
  return includePpa && available.has(fields.ppaEfficiency) ? fields.ppaEfficiency : fields.efficiency;
}

/** The single target efficiency — one column for both metric families. */
export const TARGET_EFFICIENCY_FIELD = 'target_efficiency';

/**
 * The pair of server columns the charts plot, for the current Output/Efficiency mode. One resolver
 * for both charts, the bar colour and the floor table, so an actual and a target cannot come from
 * different modes. It names columns and computes nothing.
 */
export interface PlottedFields {
  /** The measured value. */
  actual: string;
  /** Its target, for the marker and for the colour comparison. */
  target: string;
  /** True when both are 0–1 fractions and are shown as percentages. */
  isPercent: boolean;
  /** Chart heading, as legacy titled it. */
  title: string;
  /** Series names for the legend and tooltip. */
  actualLabel: string;
  targetLabel: string;
}

export function plottedFields(
  mode: ChartMode,
  metric: Metric,
  includePpa: boolean,
  available: ReadonlySet<string>,
): PlottedFields {
  if (mode === 'efficiency') {
    return {
      actual: efficiencyField(metric, includePpa, available),
      target: TARGET_EFFICIENCY_FIELD,
      isPercent: true,
      title: 'Sewing line efficiency (%)',
      actualLabel: 'Efficiency',
      targetLabel: 'Target efficiency',
    };
  }
  return {
    actual: metric === 'EINS' ? 'eins_output' : 'sfts_output',
    target: metric === 'EINS' ? 'target_eins_output' : 'target_sfts_output',
    isPercent: false,
    title: 'Sewing line output (pcs)',
    actualLabel: 'Output',
    targetLabel: 'Target output',
  };
}

const DISPLAY_DATE = 'd MMM yyyy';

/** One cell. `percent` is the departure from the shared formatter: the value is a 0–1 fraction. */
export function formatSewingCell(value: unknown, column: ReportColumn): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground/40">—</span>;
  }
  if (column.type === 'percent') {
    return `${decimal.format(Number(value) * 100)}%`;
  }
  if (column.type === 'date') {
    const parsed = parseISO(String(value));
    return isValid(parsed) ? format(parsed, DISPLAY_DATE) : String(value);
  }
  return defaultFormat(value, column);
}

/** The line-name cell on the summary: a real `<Button>`, so the drill is keyboard-reachable (§4.9). */
function lineDrillCell(
  row: SewingRow,
  onDrillToLine: (lineNo: string) => void,
): React.ReactNode {
  const name = row['show_line_no'];
  const code = row['line_no'];
  const label = typeof name === 'string' && name !== '' ? name : String(code ?? '');

  if (typeof code !== 'string' || code === '') {
    return label;
  }

  return (
    <Button
      type="button"
      variant="link"
      className="h-auto justify-start gap-1 p-0 text-xs font-normal"
      onClick={() => {
        onDrillToLine(code);
      }}
      aria-label={`Show ${label} day by day`}
    >
      <span className="truncate">{label}</span>
      <FontAwesomeIcon icon={faArrowRightLong} className="size-2.5 opacity-60" aria-hidden />
    </Button>
  );
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** The measure columns both authored layouts resolve, for the metric and PPA choice on screen. */
interface AuthoredMeasureFields {
  targetQty: string;
  targetProduced: string;
  targetAttended: string;
  targetEfficiency: string;
  actualQty: string;
  actualProduced: string;
  actualAttended: string;
  actualEfficiency: string;
  /** `'SP'` or `''`, as `summary.sql` writes it. */
  sample: string;
  /** The Actual group's heading. By Day calls this once per family, so it names the family there. */
  actualHeading: string;
}

function authoredMeasureFields(
  metric: Metric,
  includePpa: boolean,
  available: ReadonlySet<string>,
): AuthoredMeasureFields {
  const family = metric === 'EINS' ? 'eins' : 'sfts';
  const ppaAttended = `${family}_ppa_mo_work_time`;
  const ppaProduced = `${family}_ppa_output_smv`;
  // All three Actual leaves key off the efficiency that actually resolved, not off `includePpa`,
  // so produced ÷ attended always equals the efficiency printed beside them.
  const ppaResolved =
    efficiencyField(metric, includePpa, available) === METRIC_FIELDS[metric].ppaEfficiency;
  return {
    targetQty: `target_${family}_output`,
    targetProduced: 'target_output_smv',
    targetAttended: 'target_mo_work_mins',
    targetEfficiency: TARGET_EFFICIENCY_FIELD,
    actualQty: `${family}_output`,
    // `daily` declares no `*_ppa_output_smv`; asking for it would blank the cell.
    actualProduced: ppaResolved && available.has(ppaProduced) ? ppaProduced : `${family}_output_smv`,
    actualAttended: ppaResolved ? ppaAttended : `${family}_mo_work_time`,
    actualEfficiency: efficiencyField(metric, includePpa, available),
    sample: `${family}_batch_no_group`,
    actualHeading: metric === 'EINS' ? 'Actual (Endline passed)' : 'Actual (Sewing)',
  };
}

/**
 * An Actual figure, green where it beat the Target figure beside it. Not a status and not a
 * threshold (§4.6, §5.2) — it compares two server-computed numbers on the same row, via
 * `target.tsx`'s {@link standing}, so the table cannot disagree with the chart. An absent target
 * renders plain rather than as a failure. Colour is never the only channel (§4.9): the arrow
 * repeats it in shape and the `sr-only` text in words.
 */
function actualCell(
  row: SewingRow,
  column: ReportColumn,
  targetField: string,
): React.ReactNode {
  const content = formatSewingCell(row[column.field], column);
  const of = standing(toNumber(row[column.field]), toNumber(row[targetField]));
  if (of !== 'at-or-over') {
    return content;
  }
  // Arrow left, figure pushed right by `ml-auto`, so coloured and uncoloured rows align on digits.
  return (
    // `status-ok-ink`, not `status-ok`: 12px body text needs 4.5:1 and plain `status-ok` is 3.28:1.
    <span className="flex w-full items-center gap-1 text-status-ok-ink">
      <StandingArrow of={of} className="size-2.5 shrink-0" />
      <span className="sr-only">{STANDING[of].words}</span>
      <span className="ml-auto">{content}</span>
    </span>
  );
}

/** A changeover count, where zero draws as the empty placeholder. Display only — the totals row
 * and the Changeover chip still read the server's real figure. */
function changeoverCell(row: SewingRow, column: ReportColumn): React.ReactNode {
  const value = row[column.field];
  return toNumber(value) === 0 ? (
    <span className="text-muted-foreground/40">—</span>
  ) : (
    formatSewingCell(value, column)
  );
}

/**
 * A figure whose fractional part is the point — two decimal places. SMV is declared `type: number`
 * and the shared formatter would round it to whole minutes, losing what a reader is comparing.
 */
function decimalCell(row: SewingRow, column: ReportColumn): React.ReactNode {
  const value = toNumber(row[column.field]);
  return value === null ? (
    <span className="text-muted-foreground/40">—</span>
  ) : (
    decimal.format(value)
  );
}

/**
 * The brand list, deduped **for display**: `prod_brand` is a nested `GROUP_CONCAT` and repeats a
 * brand once per distinct daily combination. Changes no answer, and is what makes the column agree
 * with its filter, which offers the individual brands. Real fix is in `improvements.md`.
 */
function brandCell(row: SewingRow, column: ReportColumn): React.ReactNode {
  const raw = row[column.field];
  if (raw === null || raw === undefined || raw === '') {
    return <span className="text-muted-foreground/40">—</span>;
  }
  const brands = [
    ...new Set(
      String(raw)
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part !== ''),
    ),
  ];
  return brands.length === 0 ? (
    <span className="text-muted-foreground/40">—</span>
  ) : (
    brands.join(', ')
  );
}

/** `'SP'` is the whole vocabulary `summary.sql` emits; this renames it, and decides nothing. */
function sampleCell(value: unknown): React.ReactNode {
  return value === 'SP' ? 'Yes' : <span className="text-muted-foreground/40">—</span>;
}

interface LeafOptions {
  label: string;
  /** Present on an Actual measure: the Target field it is coloured against. */
  compareWith?: string;
  cell?: (row: SewingRow, column: ReportColumn) => React.ReactNode;
  /** Opt this column into the bottom totals row. Never set on a percent — see {@link summaryColumns}. */
  totalled?: boolean;
}

/**
 * One leaf of the authored layout, built from the server's own column metadata. The `id` stays the
 * server field name — it travels back as `SortSpec.field` and as the export's column name (§5.9),
 * both whitelisted server-side. Sortability is read off `column.sortable`, never asserted: most of
 * these fields are off the `sort:` whitelist, and a header offering an order the export cannot
 * repeat is a screen and a file that disagree.
 */
function authoredLeaf(
  byField: ReadonlyMap<string, ReportColumn>,
  field: string,
  { label, compareWith, cell, totalled = false }: LeafOptions,
  filtered: ReadonlySet<string>,
): ColumnDef<SewingRow> | null {
  const column = byField.get(field);
  if (column === undefined) {
    return null;
  }
  const numeric = column.type === 'number' || column.type === 'integer' || column.type === 'percent';
  return {
    id: field,
    accessorKey: field,
    // A filtered column keeps a plain string: `withHeaderFilters` reads the label off it.
    header: filtered.has(field)
      ? label
      : () => <span className={HEADER_TITLE}>{label}</span>,
    enableSorting: column.sortable === true,
    size: numeric ? 72 : 96,
    minSize: 48,
    meta: {
      exportLabel: label, // the export writes what the screen says, not the yaml's label (§5.9)
      align: numeric ? 'right' : 'left',
      cellClassName: numeric ? 'px-1 font-mono tabular-nums' : undefined,
      headerClassName: WRAP_HEADER,
      ...(totalled ? { total: 'sum' as const } : {}),
    },
    cell: ({ row }) =>
      cell !== undefined
        ? cell(row.original, column)
        : compareWith !== undefined
          ? actualCell(row.original, column, compareWith)
          : formatSewingCell(row.original[field], column),
  };
}

/** Drops the leaves whose field the response did not carry. */
function present(defs: (ColumnDef<SewingRow> | null)[]): ColumnDef<SewingRow>[] {
  return defs.filter((def) => def !== null);
}

/** A group heading over its children, or nothing when the response carried none of them. */
function group(header: string, children: ColumnDef<SewingRow>[]): ColumnDef<SewingRow>[] {
  return children.length === 0
    ? []
    : [
        {
          id: `group:${header}`,
          header: () => <span className={HEADER_TITLE}>{header}</span>,
          // A heading is not a field; it names no column, so nothing can order by it (§5.4).
          enableSorting: false,
          // `exportGroup` keeps the band in the file, or "Quantity" appears twice unqualified.
          meta: { headerClassName: WRAP_HEADER, exportGroup: header },
          columns: children,
        },
      ];
}

/**
 * The By Line table: identity, then Target, then Actual under the same four sub-headings, so every
 * actual sits under the number it has to beat. Presentation only (§4.3) — every cell is a complete
 * server-computed value, re-ordered and re-labelled, none combined. See `design-notes.md`.
 */
function summaryColumns(
  columns: readonly ReportColumn[],
  metric: Metric,
  includePpa: boolean,
  available: ReadonlySet<string>,
  onDrillToLine?: (lineNo: string) => void,
  groupedByLineType = false,
): ColumnDef<SewingRow>[] {
  const byField = new Map(columns.map((column) => [column.field, column]));
  const fields = authoredMeasureFields(metric, includePpa, available);

  const leaf = (field: string, options: LeafOptions) =>
    authoredLeaf(byField, field, options, SUMMARY_FILTER_SET);

  const lineIdentity = [
    leaf('building', { label: 'Building' }),
    leaf('floor', { label: 'Floor' }),
    leaf('show_line_no', {
      label: 'Line Name',
      cell:
        onDrillToLine === undefined
          ? undefined
          : (row) => lineDrillCell(row, onDrillToLine),
    }),
    leaf('line_type_name', { label: 'Line Type' }),
    leaf('prod_brand', { label: 'Brand', cell: brandCell }),
    leaf('changeover_actual_number', {
      label: 'No of Changeovers',
      cell: changeoverCell,
      totalled: true,
    }),
    leaf(fields.sample, { label: 'Sample Production', cell: (row) => sampleCell(row[fields.sample]) }),
  ];
  const identity = groupedByLineType
    ? [
        leaf('line_type_name', { label: 'Line Type' }),
        leaf('building', { label: 'Building' }),
        leaf('lines_total', { label: 'Lines', totalled: true }),
      ]
    : lineIdentity;

  const target = [
    leaf(fields.targetQty, { label: 'Quantity', totalled: true }),
    leaf(fields.targetProduced, { label: 'Produced Minutes', totalled: true }),
    leaf(fields.targetAttended, { label: 'Attended Minutes', totalled: true }),
    leaf(fields.targetEfficiency, { label: 'Efficiency' }),
  ];

  const actual = [
    leaf(fields.actualQty, { label: 'Quantity', compareWith: fields.targetQty, totalled: true }),
    leaf(fields.actualProduced, {
      label: 'Produced Minutes',
      compareWith: fields.targetProduced,
      totalled: true,
    }),
    // Never coloured: burning more line-minutes than planned is not beating a target.
    leaf(fields.actualAttended, { label: 'Attended Minutes', totalled: true }),
    leaf(fields.actualEfficiency, {
      label: 'Efficiency',
      compareWith: fields.targetEfficiency,
    }),
  ];

  return [
    ...present(identity),
    ...group('Target', present(target)),
    ...group(fields.actualHeading, present(actual)),
  ];
}

/** The Line Type table shares By Line's measure layout, using server-aggregated type rows. */
export function lineTypeSummaryColumns(
  columns: readonly ReportColumn[],
  metric: Metric,
  includePpa: boolean,
): ColumnDef<SewingRow>[] {
  const available = new Set(columns.map((column) => column.field));
  return summaryColumns(columns, metric, includePpa, available, undefined, true);
}

/**
 * The By Day table — the By Line shape on the drill-through's own grain. Presentation only (§4.3),
 * as on By Line. Which columns are dropped and why is in `design-notes.md`.
 */
function dailyColumns(
  columns: readonly ReportColumn[],
  includePpa: boolean,
  available: ReadonlySet<string>,
): ColumnDef<SewingRow>[] {
  const byField = new Map(columns.map((column) => [column.field, column]));
  // Both families side by side, so this view takes no metric — there is nothing left to toggle.
  const eins = authoredMeasureFields('EINS', includePpa, available);
  const sfts = authoredMeasureFields('SFTS', includePpa, available);
  const leaf = (field: string, options: LeafOptions) =>
    authoredLeaf(byField, field, options, DAILY_FILTER_SET);

  const identity = [
    leaf('tran_date', { label: 'Date' }),
    leaf('building', { label: 'Building' }),
    leaf('floor', { label: 'Floor' }),
    leaf('line_name', { label: 'Line Name' }),
    leaf('line_type_name', { label: 'Line Type' }),
    leaf('prod_brand', { label: 'Brand', cell: brandCell }),
    // Not totalled: an order worked five days appears on five rows, so this sums to order-days.
    leaf('order_num', { label: 'Orders' }),
    // Not totalled: a rate. Its true total is SUM(earned)/SUM(pieces), never a mean of averages.
    leaf('average_smv', { label: 'Average SMV', cell: decimalCell }),
  ];

  // Headcount, not output, so its own heading rather than swept into Actual where it would read as
  // measured against a target it does not have.
  const attendance = [
    // Not totalled: `max(manpower)` over the day's timeslots — a snapshot, so summing gives
    // person-days under a heading that reads "Manpower".
    leaf('manpower', { label: 'Manpower' }),
    // A rate, so never summed — the totals row would show the mean of a column of percentages.
    leaf('attend_rate', { label: 'Rate' }),
    // Attended minutes is one figure for both families, not a per-family one; shown and totalled
    // once here rather than double-counted under both Actual headings.
    leaf(eins.actualAttended, { label: 'Attended Minutes', totalled: true }),
  ];

  // One Target section for two Actual sections: `target_eins_output` and `target_sfts_output` are
  // separate names on the wire for one server expression.
  const target = [
    leaf(eins.targetQty, { label: 'Quantity', totalled: true }),
    leaf(eins.targetProduced, { label: 'Produced Minutes', totalled: true }),
    leaf(eins.targetAttended, { label: 'Attended Minutes', totalled: true }),
    leaf(eins.targetEfficiency, { label: 'Efficiency' }),
  ];

  const actualFor = (fields: ReturnType<typeof authoredMeasureFields>) => [
    leaf(fields.actualQty, { label: 'Quantity', compareWith: fields.targetQty, totalled: true }),
    leaf(fields.actualProduced, {
      label: 'Produced Minutes',
      compareWith: fields.targetProduced,
      totalled: true,
    }),
    leaf(fields.actualEfficiency, {
      label: 'Efficiency',
      compareWith: fields.targetEfficiency,
    }),
  ];

  const quality = [
    leaf('inline_defect_num', { label: 'Inline defects', totalled: true }),
    leaf('endline_defect_num', { label: 'Endline defects', totalled: true }),
    leaf('wft_output', { label: 'WFT output', totalled: true }),
    // A rate, like Attendance — its true total is a ratio of two sums and stays in SQL (§4.3).
    leaf('wft', { label: 'WFT' }),
  ];

  return [
    ...present(identity),
    ...group('Target', present(target)),
    ...group('Attendance (actual)', present(attendance)),
    // Process order, not alphabetical: sewing happens before the endline check that follows it.
    ...group(sfts.actualHeading, present(actualFor(sfts))),
    ...group(eins.actualHeading, present(actualFor(eins))),
    ...group('Quality', present(quality)),
  ];
}

/** By MO keeps By Day's attendance and process groups without its line-day target or quality. */
function moColumns(columns: readonly ReportColumn[]): ColumnDef<SewingRow>[] {
  const byField = new Map(columns.map((column) => [column.field, column]));
  const leaf = (field: string, options: LeafOptions) =>
    authoredLeaf(byField, field, options, MO_FILTER_SET);

  const identity = [
    leaf('tran_date', { label: 'Date' }),
    leaf('building', { label: 'Building' }),
    leaf('floor', { label: 'Floor' }),
    leaf('line_name', { label: 'Line Name' }),
    leaf('line_type_name', { label: 'Line Type' }),
    leaf('prod_brand', { label: 'Brand', cell: brandCell }),
    leaf('mo', { label: 'MO' }),
    leaf('cpo', { label: 'CPO' }),
    leaf('working_no', { label: 'Working No' }),
    leaf('article_no', { label: 'Article' }),
    leaf('crd', { label: 'CRD' }),
    leaf('psdd', { label: 'PSDD' }),
    // Not totalled: a property of the order with no date key, so it repeats on every day it ran.
    leaf('fr_order_qty', { label: 'Order Qty' }),
    // Not totalled: a rate. Its true total is SUM(earned)/SUM(pieces), which stays in SQL (§4.3).
    leaf('smv', { label: 'SMV', cell: decimalCell }),
  ];

  const attendance = [
    // One line-day headcount repeats on every MO, so it must never be summed.
    leaf('manpower', { label: 'Manpower' }),
    leaf('sfts_mo_work_time', { label: 'Attended Minutes (Sewing)', totalled: true }),
    leaf('eins_mo_work_time', { label: 'Attended Minutes (Endline)', totalled: true }),
  ];

  const sewing = [
    leaf('sfts_qty', { label: 'Quantity', totalled: true }),
    leaf('sfts_output_smv', { label: 'Produced Minutes', totalled: true }),
    leaf('sfts_efficiency', { label: 'Efficiency' }),
  ];

  const endline = [
    leaf('eins_qty', { label: 'Quantity', totalled: true }),
    leaf('eins_output_smv', { label: 'Produced Minutes', totalled: true }),
    leaf('eins_efficiency', { label: 'Efficiency' }),
  ];

  return [
    ...present(identity),
    ...group('Attendance (actual)', present(attendance)),
    ...group('Actual (Sewing)', present(sewing)),
    ...group('Actual (Endline passed)', present(endline)),
  ];
}

/**
 * The Changeover table — legacy's CHANGEOVER QTY modal, as a tab. Plan counts changeovers
 * scheduled in the window; Actual counts scheduled changeovers observed to happen, with no lower
 * date bound — so a floor can legitimately read plan 0 against actual 1. See `design-notes.md`.
 */
function changeoverColumns(columns: readonly ReportColumn[]): ColumnDef<SewingRow>[] {
  const byField = new Map(columns.map((column) => [column.field, column]));
  const leaf = (field: string, options: LeafOptions) =>
    authoredLeaf(byField, field, options, CHANGEOVER_FILTER_SET);

  /** One transfer type's pair, under its own heading. */
  const pair = (suffix: string) =>
    present([
      leaf(`plan_${suffix}`, { label: 'Plan', totalled: true }),
      leaf(`actual_${suffix}`, {
        label: 'Actual',
        compareWith: `plan_${suffix}`,
        totalled: true,
      }),
    ]);

  return [
    ...present([
      leaf('building', { label: 'Building' }),
      leaf('floor', { label: 'Floor' }),
      leaf('lines_changed', { label: 'Lines Changed Over', totalled: true }),
    ]),
    ...group('CO A', pair('a')),
    ...group('CO B', pair('b')),
    ...group('CO C', pair('c')),
    ...group('CO D', pair('d')),
    ...group('Total', pair('total')),
  ];
}

/** The identity columns the By Line table offers a header filter on. */
export const SUMMARY_FILTER_FIELDS = ['building', 'floor', 'show_line_no', 'line_type_name', 'prod_brand'] as const;

/** The same, for Changeover. Filtering Building to one value is how a reader gets its subtotal. */
export const CHANGEOVER_FILTER_FIELDS = ['building', 'floor'] as const;
const CHANGEOVER_FILTER_SET: ReadonlySet<string> = new Set(CHANGEOVER_FILTER_FIELDS);

/**
 * The By MO columns that carry a header filter — the identifying half of the row. The measures
 * after it are deliberately outside: a multi-select over every distinct SMV answers no question.
 */
export const MO_FILTER_FIELDS = [
  'tran_date',
  'building',
  'floor',
  'line_name',
  'line_type_name',
  'prod_brand',
  'mo',
  'cpo',
  'working_no',
  'article_no',
  'crd',
  'psdd',
] as const;
const MO_FILTER_SET: ReadonlySet<string> = new Set(MO_FILTER_FIELDS);

/**
 * How a header-filter option is labelled. Only dates differ from their raw value. The value itself
 * is untouched — still what the row is matched against, what travels in the URL, and what sorts.
 */
export function filterOptionLabel(
  columns: readonly ReportColumn[],
): (field: string, value: string) => string {
  const byField = new Map(columns.map((column) => [column.field, column]));
  return (field, value) => {
    if (byField.get(field)?.type !== 'date') {
      return value;
    }
    const parsed = parseISO(value);
    return isValid(parsed) ? format(parsed, DISPLAY_DATE) : value;
  };
}

/** The same, for By Day. The line's readable name is `line_name` here, not `show_line_no`. */
export const DAILY_FILTER_FIELDS = ['tran_date', 'building', 'floor', 'line_name', 'line_type_name'] as const;
const DAILY_FILTER_SET: ReadonlySet<string> = new Set(DAILY_FILTER_FIELDS);

/**
 * Of those, the ones whose cell holds a comma-joined list. `prod_brand` is a `GROUP_CONCAT`, so its
 * filter offers the individual brands and a row matches when it carries any one of them.
 */
export const SUMMARY_LIST_FILTER_FIELDS: ReadonlySet<string> = new Set(['prod_brand']);

/** Membership test for {@link SUMMARY_FILTER_FIELDS}, used when building each leaf's header. */
const SUMMARY_FILTER_SET: ReadonlySet<string> = new Set(SUMMARY_FILTER_FIELDS);

/** The `totals` view fields the totals row reads. Structurally satisfied by `SewingTotals`. */
export interface SummaryTotalsRow {
  eins_efficiency: number | null;
  sfts_efficiency: number | null;
  eins_ppa_efficiency: number | null;
  sfts_ppa_efficiency: number | null;
  target_efficiency: number | null;
}

/**
 * The two efficiency cells of the totals row, **read from ADB rather than derived here**: it is
 * `SUM(produced)/SUM(attended)`, and totalling a percentage column gives the mean of the ratios —
 * a different number that looks plausible. The caller must withhold this whenever a chip or a
 * column filter narrows the table, since `totals` answers for every line and not for a subset.
 */
export function summaryTotalValues(
  metric: Metric,
  includePpa: boolean,
  available: ReadonlySet<string>,
  totals: SummaryTotalsRow | null,
): Record<string, React.ReactNode> {
  if (totals === null) {
    return {};
  }
  const asPercent = (value: number | null): React.ReactNode | undefined =>
    value === null ? undefined : `${decimal.format(value * 100)}%`;

  const actualField = efficiencyField(metric, includePpa, available);
  // Written out rather than indexed, so a renamed column fails to compile.
  const actualValue =
    actualField === 'eins_efficiency'
      ? totals.eins_efficiency
      : actualField === 'eins_ppa_efficiency'
        ? totals.eins_ppa_efficiency
        : actualField === 'sfts_ppa_efficiency'
          ? totals.sfts_ppa_efficiency
          : totals.sfts_efficiency;

  const values: Record<string, React.ReactNode> = {};
  const actual = asPercent(actualValue);
  if (actual !== undefined) values[actualField] = actual;
  // One `target_efficiency` for both families, so this does not depend on the metric selector.
  const target = asPercent(totals.target_efficiency);
  if (target !== undefined) values[TARGET_EFFICIENCY_FIELD] = target;
  return values;
}

/** Ratios for the Line Type table's total row are returned by its grouped SQL query. */
export function lineTypeTotalValues(
  metric: Metric,
  includePpa: boolean,
  available: ReadonlySet<string>,
  row: SewingRow | undefined,
): Record<string, React.ReactNode> {
  if (row === undefined) return {};
  const actualField = efficiencyField(metric, includePpa, available);
  const asPercent = (value: unknown): React.ReactNode | undefined => {
    const number = toNumber(value);
    return number === null ? undefined : `${decimal.format(number * 100)}%`;
  };
  const values: Record<string, React.ReactNode> = {};
  const target = asPercent(row['total_target_efficiency']);
  const actual = asPercent(row[`total_${actualField}`]);
  if (target !== undefined) values[TARGET_EFFICIENCY_FIELD] = target;
  if (actual !== undefined) values[actualField] = actual;
  return values;
}

export interface SewingColumnsOptions {
  /** Straight from the response envelope — the client renders the structure it is given. */
  columns: readonly ReportColumn[];
  view: ReportViewName;
  metric: Metric;
  includePpa: boolean;
  /** Summary only: turns the line name into the drill-through into the `daily` view. */
  onDrillToLine?: (lineNo: string) => void;
}

export function sewingColumns({
  columns,
  view,
  metric,
  includePpa,
  onDrillToLine,
}: SewingColumnsOptions): ColumnDef<SewingRow>[] {
  const available = new Set(columns.map((column) => column.field));

  if (view === 'summary') {
    return summaryColumns(columns, metric, includePpa, available, onDrillToLine);
  }
  if (view === 'daily') {
    return dailyColumns(columns, includePpa, available);
  }
  if (view === 'changeover') {
    return changeoverColumns(columns);
  }

  // `mo` is a row-level listing with no metric and no target, so it takes neither argument.
  return moColumns(columns);
}

/**
 * One row of the transposed floor table: a measure name, the type its cells are rendered as, and
 * one already-computed cell per line, keyed by `line_no`.
 */
export type FloorMatrixRow = Record<string, unknown> & {
  measure: string;
  valueType: 'number' | 'percent';
};

/**
 * Legacy's per-floor table: line numbers across the top, one row for output and one for efficiency.
 * A re-layout of complete server-computed cells and nothing more (§4.3) — the caller must have
 * established the result is complete. Legacy's 0.60/0.40 colour bands are invented thresholds and
 * stay unported (§5.3); they are recorded in `improvements.md` as a question for the owner.
 */
export function floorMatrix(
  lines: readonly SewingRow[],
  metric: Metric,
  includePpa: boolean,
  available: ReadonlySet<string>,
): FloorMatrixRow[] {
  const outputField = metric === 'EINS' ? 'eins_output' : 'sfts_output';
  const efficiency = efficiencyField(metric, includePpa, available);

  const build = (
    measure: string,
    field: string,
    valueType: 'number' | 'percent',
  ): FloorMatrixRow => {
    const row: FloorMatrixRow = { measure, valueType };
    for (const line of lines) {
      row[String(line['line_no'])] = line[field];
    }
    return row;
  };

  return [
    build('Output (pcs)', outputField, 'number'),
    build('Efficiency', efficiency, 'percent'),
  ];
}

/** Columns for {@link floorMatrix}: the measure name, then one column per line. */
export function floorMatrixColumns(
  lines: readonly SewingRow[],
): ColumnDef<FloorMatrixRow>[] {
  const measure: ColumnDef<FloorMatrixRow> = {
    id: 'measure',
    accessorKey: 'measure',
    header: 'Line',
    enableSorting: false,
    meta: { align: 'left', cellClassName: 'font-medium whitespace-nowrap' },
  };

  // Column order is the server's row order, taken as given: `lines` is the array the floor chart
  // plots, so re-sorting here would put the matrix columns out of step with the bars.
  return [
    measure,
    ...lines.map<ColumnDef<FloorMatrixRow>>((line) => {
      const field = String(line['line_no']);
      const label = String(line['show_line_no'] ?? field);
      return {
        id: field,
        accessorKey: field,
        header: label,
        // Nothing here is sortable — the matrix has two rows and its columns are lines, not fields.
        enableSorting: false,
        meta: { align: 'right', cellClassName: 'px-1 font-mono tabular-nums' },
        cell: ({ row }) =>
          formatSewingCell(row.original[field], {
            field,
            label,
            type: row.original.valueType === 'percent' ? 'percent' : 'number',
          }),
      };
    }),
  ];
}

/** A stable row identity per view, so React keeps a row's DOM across a sort change. */
export function rowIdFor(view: ReportViewName): (row: SewingRow, index: number) => string {
  return (row, index) => {
    // Changeover's grain is the floor: `build_floor` is the key both legs of the query group on.
    if (view === 'changeover') {
      return String(row['build_floor'] ?? index);
    }
    const line = String(row['line_no'] ?? index);
    if (view === 'summary') {
      return line;
    }
    const date = String(row['tran_date'] ?? '');
    return view === 'daily' ? `${date}|${line}` : `${date}|${line}|${String(row['mo'] ?? index)}`;
  };
}
